import React, { useMemo } from "react";
import clsx from "clsx";
import ReactECharts from "echarts-for-react";
import useDarkMode from "../../hooks/useDarkMode";
import { ICON_SIZES } from "../../helpers/constants";
import useMobile from "../../hooks/useMobile";
import useCharts from "../../hooks/useCharts";
import { formatCurrency, formatLongDate } from "src/helpers/utils";
import useCurrentAccount from "src/hooks/useCurrentAccount";
import moment from "moment";
import { EReportsChartKeys } from "./chartIds";
import NoTableData from "../NoTableData/NoTableData";
import { useTranslation } from "react-i18next";

interface ICommonBarLineChart {
  series: Array<unknown>;
  categories: Array<unknown>;
  chartId: EReportsChartKeys;
}

const CommonBarLineChart: React.FC<ICommonBarLineChart> = ({
  series,
  categories,
  chartId,
}) => {
  const { isEnabled: isDarkMode } = useDarkMode();
  const { t } = useTranslation();
  const { currencySymbol } = useCurrentAccount();
  const { isMobile } = useMobile();
  const { updateChart, updateZoom, chartPreferences } = useCharts();
  const zoomLevel = chartPreferences.zoom?.[chartId] || { start: 0, end: 20 };

  const processedSeries = useMemo(() => {
    return series.map((serie: any) => {
      if (serie.type === "line") {
        return {
          ...serie,
          symbol: "circle",
          smooth: true,
          symbolSize: isMobile ? 25 : 35,
          lineStyle: {
            ...(serie.lineStyle || {}),
            width: 2,
          },
          itemStyle: {
            ...(serie.itemStyle || {}),
            opacity: 0.8, // aquí defines la opacidad del símbolo
          },
        };
      }

      return serie;
    });
  }, [series, isMobile]);

  const options: any = useMemo(
    () => ({
      backgroundColor: "transparent",
      animationDuration: 1000,
      animationDelay: 500,
      animationEasing: "cubicOut",
      grid: { top: 50, right: 8, bottom: 24, left: 50 },

      xAxis: {
        type: "category",
        data: categories,
        min: 0,
        silent: false,
        splitLine: {
          show: false,
        },
        splitArea: {
          show: false,
        },
      },
      legend: {
        left: "left",
        orient: "horizontal",
      },
      toolbox: {
        show: true,
        feature: {
          magicType: {
            type: ["bar", "line"],
            iconStatus: {
              line: chartPreferences.type === "line" ? "emphasis" : "normal",
              bar: chartPreferences.type === "bar" ? "emphasis" : "normal",
            },
            title: {
              line: t("charts.line_chart"),
              bar: t("charts.bar_chart"),
            },
          },
          // restore: {},
          saveAsImage: {
            name: "eXpenser",
            backgroundColor: "#fff",
            title: t("charts.download_chart"),
          },
        },
        itemSize: ICON_SIZES.SM,
        itemGap: 14,
      },
      yAxis: {
        type: "value",
        axisLabel: {
          formatter: (value: any) => {
            const units = [
              "",
              "K",
              "M",
              "B",
              "T",
              "Qa",
              "Qi",
              "Sx",
              "Sp",
              "Oc",
            ];
            let unitIndex = 0;
            while (value >= 1000 && unitIndex < units.length - 1) {
              value /= 1000;
              unitIndex++;
            }
            return `${value.toFixed(0)}${units[unitIndex]}`;
          },
        },
      },
      series: processedSeries,
      tooltip: {
        triggerOn: "click",
        // appendTo: document?.body,
        confine: true,
        trigger: "axis",
        // alwaysShowContent: true,
        hideDelay: 1000 * 10,
        backgroundColor: isDarkMode ? "#fff" : "#fff",
        borderColor: "transparent",
        textStyle: {
          color: isDarkMode ? "#333333" : "#1e3a8a",
        },
        axisPointer: {
          type: "cross",
          snap: true,
          label: {
            precision: "2",
            fontSize: isMobile ? 14 : 14,
            fontWeight: "bold",
            backgroundColor: isDarkMode ? "#a855f795" : "#ff000090",
            formatter: (params: any) => {
              if (params?.axisDimension === "y") {
                return formatCurrency({
                  value: params?.value,
                  currency: currencySymbol,
                });
              }

              if (chartId === "latest-transactions-chart") {
                if (!moment(params?.value)?.isValid()) {
                  return params?.value;
                } else {
                  return formatLongDate(params?.value);
                }
              }

              return params?.value;
            },
          },
        },
        valueFormatter: (value: any) =>
          formatCurrency({ value, currency: currencySymbol }),
        extraCssText: "z-index: 10",
      },
      dataZoom: [
        {
          type: "slider",
          show: true,
          start: zoomLevel.start,
          end: zoomLevel.end,
          zoomOnMouseWheel: true,
          // zoomLock: true,
          handleSize: "50px",
        },
        {
          type: "inside",
          start: zoomLevel.start,
          end: zoomLevel.end,
          zoomOnMouseWheel: false, // Deshabilitar zoom con la rueda del mouse (trackpad)
          moveOnMouseMove: true, // Permite mover el gráfico con el mouse horizontalmente
          moveOnMouseWheel: true, // Permite hacer scroll horizontal con el mouse
          zoomLock: true, // Bloquea el zoom en ambas direcciones
        },
      ],
    }),
    [series, categories, isMobile, isDarkMode]
  );

  const hasData = processedSeries.some(
    (serie: any) =>
      Array.isArray(serie.data) &&
      serie.data.length > 0 &&
      serie.data.some((value: any) => {
        return value !== null && value !== undefined && value !== 0;
      })
  );

  const chartType = useMemo(() => {
    return (series?.[0] as { type?: string })?.type || null;
  }, [series]);

  const isLineChart = chartType === "line";

  return hasData ? (
    <div className={clsx(["block"])}>
      <ReactECharts
        option={options}
        notMerge={true}
        lazyUpdate={true}
        theme={isDarkMode ? "dark" : "light"}
        onEvents={{
          magictypechanged: (params: any) => {
            const { currentType } = params;
            updateChart({ type: currentType });
          },
          dataZoom: (params: any) => {
            const { start, end } = params;
            updateZoom(chartId, start, end);
          },
        }}
      />
    </div>
  ) : (
    <NoTableData
      // icon={isLineChart ? "MdShowChart" : "MdBarChart"}
      icon={"MdBarChart"}
      text="No hay datos para generar el gráfico"
    />
  );
};

export default React.memo(CommonBarLineChart);
